import React from 'react';

import { Typography } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';
import { AsyncThunk } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { createConflictErrorHandler,useDeleteDialogHandler } from 'common/hooks/useDeleteDialogHandler';
import { DeleteDialogContentProps } from 'common/types/TypeDefs';
import { useSnackbar } from 'notistack';

export interface DeleteInfoItem {
    label: string;
    value: string | undefined;
}

interface FilterResult {
    keywords: string[];
    dropdowns?: {
        status: {
            keys: string[];
        };
    };
}

export interface MembershipDeleteDialogContentProps<TRow, TParams> extends DeleteDialogContentProps<TRow> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deleteThunk: AsyncThunk<any, TParams, any>;
    entityName: string;
    successMessage: string;
    conflictMessage?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectFilters: (state: any) => FilterResult;
    infoItems: DeleteInfoItem[];
    buildDeleteParams: (params: {
        itemId: string;
        editingRow: TRow | undefined;
        skip: number;
        take: number;
        search: string | undefined;
        sortModel: GridSortModel | undefined;
        statusKeys: string[];
    }) => TParams;
}

export function MembershipDeleteDialogContent<
    TRow extends Record<string, unknown>,
    TParams
>({
    itemId,
    editingRow,
    paginationInfo,
    customDeleteTitle,
    deleteThunk,
    entityName,
    successMessage,
    conflictMessage = '',
    selectFilters,
    infoItems,
    buildDeleteParams,
}: Readonly<MembershipDeleteDialogContentProps<TRow, TParams>>): React.ReactElement {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const { rowsOnPage } = paginationInfo ?? {};
    const { currentPage = 0, rowsPerPage = 0, sortModel } = paginationGridData ?? {};
    const filterProp = useAppSelector(selectFilters);
    const { keywords } = filterProp;

    const customErrorHandler = createConflictErrorHandler(
        conflictMessage,
        enqueueSnackbar
    );

    const { processing, handleDelete } = useDeleteDialogHandler({
        deleteThunk,
        entityName,
        successMessage,
        onSuccess: (result) => {
            dispatch(setPaginationGridData({
                rows: result.rows,
                rowsCount: result.count,
                currentPage: result.skipped / rowsPerPage,
                rowsPerPage,
                sortModel,
            }));
        },
        onError: customErrorHandler,
    });

    const deleteCallback = () => {
        if (itemId) {
            const skip = (rowsOnPage === 1) && (currentPage > 0)
                ? ((currentPage - 1) * rowsPerPage)
                : (currentPage * rowsPerPage);

            const params = buildDeleteParams({
                itemId: String(itemId),
                editingRow,
                skip,
                take: rowsPerPage,
                search: keywords[0],
                sortModel,
                statusKeys: filterProp.dropdowns?.status?.keys ?? [],
            });

            handleDelete(params);
        }
    };

    return (
        <DeleteDialog deleteCallback={deleteCallback} processing={processing} customDeleteTitle={customDeleteTitle}>
            {infoItems.map((item) => (
                <Typography key={item.label} color="info.light" fontSize='0.9rem' fontWeight='550'>
                    {item.label}: <Typography component="span" color="text.primary" fontWeight="400">{item.value}</Typography>
                </Typography>
            ))}
        </DeleteDialog>
    );
}
